import React from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { useLang } from "@/context/LanguageContext";
import { Phone, ArrowRight, Star } from "lucide-react";
import { trackCall } from "@/lib/track";

const HERO_IMG = "https://customer-assets-0z36b82j.emergentagent.net/job_fea37d4b-bffe-409a-b844-1d302e7da01c/artifacts/zk0wif0n_generated-1%20%281%29.jpeg";

export default function Hero() {
  const { t } = useLang();
  return (
    <section className="relative overflow-hidden pt-28 pb-16 md:pt-36 md:pb-24">
      <div className="absolute inset-0">
        <img src={HERO_IMG} alt="Flores on the move truck" className="w-full h-full object-cover object-center opacity-60" />
        <div className="absolute inset-0 bg-gradient-to-r from-background via-background/85 to-background/40" />
        <div className="absolute inset-0 bg-background/40" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
          <div className="inline-flex items-center gap-2 rounded-full border border-primary/40 bg-primary/10 px-4 py-1.5 text-xs uppercase tracking-widest text-primary font-bold" data-testid="hero-eyebrow">
            <Star className="h-3.5 w-3.5 fill-primary" /> {t.hero.eyebrow}
          </div>

          <h1
            className="mt-6 text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter leading-[0.95] text-shadow-strong"
            style={{ fontFamily: "Outfit" }}
            data-testid="hero-title"
          >
            {t.hero.title1}
            <br />
            <span className="text-primary">{t.hero.title2}</span>
          </h1>

          <p className="mt-6 text-lg md:text-xl text-muted-foreground max-w-xl" data-testid="hero-subtitle">
            {t.hero.subtitle}
          </p>

          <div className="mt-8 flex flex-wrap gap-4">
            <Button
              size="lg"
              className="rounded-full bg-primary hover:bg-primary/90 text-primary-foreground text-base font-semibold px-8 py-6 group"
              onClick={() => document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" })}
              data-testid="hero-cta-quote"
            >
              {t.hero.cta}
              <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
            </Button>
            <a
              href="tel:+13316459072"
              onClick={() => trackCall("hero_cta")}
              className="inline-flex items-center gap-2 rounded-full border border-border hover:border-primary hover:text-primary px-6 py-3 text-base font-semibold transition-colors"
              data-testid="hero-cta-call"
            >
              <Phone className="h-4 w-4" />
              {t.hero.cta2}
            </a>
          </div>

          <div className="mt-10 flex items-center gap-6 text-sm text-muted-foreground">
            <div className="flex items-center gap-2">
              <div className="flex">{Array.from({ length: 5 }).map((_, i) => <Star key={i} className="h-4 w-4 fill-primary text-primary" />)}</div>
              <span>5.0 • Thumbtack</span>
            </div>
            <div className="h-4 w-px bg-border" />
            <div>Roselle, IL</div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: 40 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="hidden lg:block"
        >
          <div className="relative">
            <div className="absolute -inset-4 bg-primary/20 blur-3xl rounded-full" />
            <div className="relative rounded-2xl border border-border bg-card/70 backdrop-blur-md p-8">
              <div className="text-xs uppercase tracking-widest text-primary font-bold">Instant Quote</div>
              <div className="mt-2 text-3xl font-bold" style={{ fontFamily: "Outfit" }}>Call us now</div>
              <a href="tel:+13316459072" onClick={() => trackCall("hero_card")} className="mt-4 flex items-center gap-3 text-4xl font-black text-primary hover:opacity-80 transition-opacity">
                <Phone className="h-8 w-8" /> (331) 645-9072
              </a>
              <div className="mt-6 grid grid-cols-2 gap-4 text-sm">
                <div className="rounded-lg border border-border p-4">
                  <div className="text-2xl font-bold">200+</div>
                  <div className="text-muted-foreground">Successful moves</div>
                </div>
                <div className="rounded-lg border border-border p-4">
                  <div className="text-2xl font-bold">5.0★</div>
                  <div className="text-muted-foreground">Average rating</div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
